import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CompactDataGrid } from "../../../components/data/CompactDataGrid";
import { StatusChip } from "../../../components/data/StatusChip";
import { TableToolbar } from "../../../components/data/TableToolbar";
import { PageWrapper } from "../../../components/layout/PageWrapper";

const shops = ["Harare Central", "Avondale", "Borrowdale", "Bulawayo CBD"];

const initialClients = [
  {
    id: "pos-hre-001",
    label: "Front till 01",
    terminalId: "HRE-CTR-T01",
    shop: "Harare Central",
    version: "2.4.1",
    requestsToday: 318,
    lastSeen: "Now",
    isOnline: true,
    isActive: true,
  },
  {
    id: "pos-hre-002",
    label: "Front till 02",
    terminalId: "HRE-CTR-T02",
    shop: "Harare Central",
    version: "2.4.1",
    requestsToday: 291,
    lastSeen: "1 min ago",
    isOnline: true,
    isActive: true,
  },
  {
    id: "pos-avd-001",
    label: "Main till",
    terminalId: "HRE-AVD-T01",
    shop: "Avondale",
    version: "2.4.0",
    requestsToday: 214,
    lastSeen: "4 min ago",
    isOnline: true,
    isActive: true,
  },
  {
    id: "pos-brw-001",
    label: "Restaurant till",
    terminalId: "HRE-BRW-T01",
    shop: "Borrowdale",
    version: "2.3.8",
    requestsToday: 176,
    lastSeen: "18 min ago",
    isOnline: false,
    isActive: true,
  },
  {
    id: "pos-byo-001",
    label: "Main till",
    terminalId: "BYO-CBD-T01",
    shop: "Bulawayo CBD",
    version: "2.3.8",
    requestsToday: 0,
    lastSeen: "12 Jul 2026",
    isOnline: false,
    isActive: false,
  },
];

const emptyClient = {
  label: "",
  terminalId: "",
  shop: shops[0],
  allowedIp: "",
  isActive: true,
};

export function PosClientsPage() {
  const [clients, setClients] = useState(initialClients);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState(emptyClient);

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();

    return clients.filter((client) => {
      const matchesTerm =
        !term ||
        client.label.toLowerCase().includes(term) ||
        client.terminalId.toLowerCase().includes(term) ||
        client.shop.toLowerCase().includes(term);
      const matchesStatus =
        status === "all" ||
        (status === "online" && client.isOnline) ||
        (status === "offline" && !client.isOnline) ||
        (status === "disabled" && !client.isActive);

      return matchesTerm && matchesStatus;
    });
  }, [clients, search, status]);

  const openCreate = () => {
    setEditingClient(null);
    setForm(emptyClient);
    setDrawerOpen(true);
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setForm({
      label: client.label,
      terminalId: client.terminalId,
      shop: client.shop,
      allowedIp: "",
      isActive: client.isActive,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingClient(null);
    setForm(emptyClient);
  };

  const submitClient = (event) => {
    event.preventDefault();
    if (!form.label.trim() || !form.terminalId.trim()) {
      toast.error("Client label and terminal ID are required.");
      return;
    }

    if (editingClient) {
      setClients((current) =>
        current.map((client) =>
          client.id === editingClient.id
            ? {
                ...client,
                label: form.label.trim(),
                terminalId: form.terminalId.trim().toUpperCase(),
                shop: form.shop,
                isActive: form.isActive,
              }
            : client,
        ),
      );
      toast.success("POS client updated in the interface prototype.");
    } else {
      setClients((current) => [
        {
          id: window.crypto?.randomUUID?.() || String(Date.now()),
          label: form.label.trim(),
          terminalId: form.terminalId.trim().toUpperCase(),
          shop: form.shop,
          version: "Not connected",
          requestsToday: 0,
          lastSeen: "Never",
          isOnline: false,
          isActive: form.isActive,
        },
        ...current,
      ]);
      toast.success("POS client added to the interface prototype.");
    }

    closeDrawer();
  };

  const columns = useMemo(
    () => [
      {
        field: "label",
        headerName: "Client",
        minWidth: 210,
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {params.row.label}
            </Typography>
            <Typography noWrap color="text.secondary" sx={{ mt: 0.1, fontSize: "0.75rem" }}>
              {params.row.terminalId}
            </Typography>
          </Box>
        ),
      },
      {
        field: "shop",
        headerName: "Shop",
        minWidth: 170,
        flex: 0.8,
      },
      {
        field: "version",
        headerName: "Version",
        width: 105,
      },
      {
        field: "requestsToday",
        headerName: "Requests today",
        width: 118,
      },
      {
        field: "lastSeen",
        headerName: "Last seen",
        minWidth: 110,
        flex: 0.5,
      },
      {
        field: "status",
        headerName: "Status",
        width: 92,
        renderCell: (params) => {
          const label = !params.row.isActive
            ? "Disabled"
            : params.row.isOnline
              ? "Online"
              : "Offline";
          const state = !params.row.isActive
            ? "disabled"
            : params.row.isOnline
              ? "online"
              : "offline";

          return <StatusChip label={label} status={state} />;
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 80,
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.25} justifyContent="flex-end" sx={{ width: "100%" }}>
            <Tooltip title="Rotate API key">
              <IconButton
                aria-label={`Rotate key for ${params.row.label}`}
                onClick={() => toast.info("Key rotation will be connected to the backend API.")}
              >
                <KeyOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit POS client">
              <IconButton
                aria-label={`Edit ${params.row.label}`}
                onClick={() => openEdit(params.row)}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [clients],
  );

  return (
    <PageWrapper>
      <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
        <TableToolbar title="POS clients" count={filteredClients.length}>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search clients"
            aria-label="Search POS clients"
            sx={{ width: { xs: "100%", sm: 220 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter POS clients by status"
            sx={{ width: 112 }}
          >
            <MenuItem value="all">All status</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            New client
          </Button>
        </TableToolbar>

        <Divider />
        <CompactDataGrid
          rows={filteredClients}
          columns={columns}
          height="calc(100vh - 113px)"
          sx={{ border: 0 }}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "100%", sm: 400 },
              maxWidth: "100vw",
              borderLeft: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        <Box component="form" onSubmit={submitClient} sx={{ display: "flex", minHeight: "100%", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              minHeight: 52,
              px: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                {editingClient ? "Edit POS client" : "New POS client"}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.15, fontSize: "0.75rem" }}>
                POS client registration
              </Typography>
            </Box>
            <IconButton aria-label="Close POS client form" onClick={closeDrawer}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Stack spacing={1.25} sx={{ p: 1.5, flex: 1 }}>
            <TextField
              label="Client label"
              value={form.label}
              onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
              placeholder="Front till 01"
              fullWidth
              autoFocus
            />
            <TextField
              label="Terminal ID"
              value={form.terminalId}
              onChange={(event) =>
                setForm((current) => ({ ...current, terminalId: event.target.value }))
              }
              placeholder="HRE-CTR-T01"
              fullWidth
            />
            <TextField
              label="Shop"
              select
              value={form.shop}
              onChange={(event) => setForm((current) => ({ ...current, shop: event.target.value }))}
              fullWidth
            >
              {shops.map((shop) => (
                <MenuItem key={shop} value={shop}>
                  {shop}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Allowed IP address"
              value={form.allowedIp}
              onChange={(event) =>
                setForm((current) => ({ ...current, allowedIp: event.target.value }))
              }
              placeholder="Optional"
              helperText="Use the backend to enforce an IP allowlist."
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={form.isActive}
                  onChange={(_, checked) => setForm((current) => ({ ...current, isActive: checked }))}
                />
              }
              label="Active client"
              sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.8125rem" } }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            justifyContent="flex-end"
            sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button variant="outlined" color="secondary" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingClient ? "Save changes" : "Register client"}
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </PageWrapper>
  );
}
